import { useEffect, useState } from "react";

export type CenterFriendlyNavCopy = {
  label: string;
  description: string;
  help: string;
};

const CENTER_NAV_COPY: Record<string, CenterFriendlyNavCopy> = {
  today: {
    label: "오늘",
    description: "오늘 먼저 볼 학생과 업무를 확인",
    help: "오늘 처리할 핵심 업무와 주의가 필요한 학생을 한눈에 확인합니다."
  },
  operations: {
    label: "오늘 할 일",
    description: "미처리 업무·알림·마감업무 확인",
    help: "미처리 업무, 마감 임박 항목, 일괄작업과 인수인계를 모아봅니다."
  },
  reports: {
    label: "리포트 보기",
    description: "학교 운영현황과 제출자료 확인",
    help: "학교 진로교육 운영 현황과 제출용 통계를 확인하고 보고서를 준비합니다."
  },
  learners: {
    label: "학생 보기",
    description: "학생의 진로기록을 한 화면에서 확인",
    help: "학생의 성장기록, 검사, 상담, 활동, Case와 배지를 통합해서 확인합니다."
  },
  "student-lifecycle": {
    label: "학적 이동 관리",
    description: "진급·전학·졸업 이력을 안전하게 관리",
    help: "전학, 진급, 재학년, 졸업 등 학생의 학적 변화를 관리합니다."
  },
  roster: {
    label: "명단·반 편성",
    description: "학생 명단과 다음 학년도 반을 준비",
    help: "학생 명단을 불러오고 학년·반 기준으로 정리하거나 차년도 반편성을 준비합니다."
  },
  classes: {
    label: "상담·학급 관리",
    description: "상담기록·담당교사·학급현황 관리",
    help: "학급 담당교사와 학생 배정, 상담 일정과 후속조치를 관리합니다."
  },
  assessments: {
    label: "진로검사 보내기",
    description: "학생에게 검사를 배정하고 진행상태 확인",
    help: "검사를 선택해 학년·학급·학생에게 보내고 완료 현황을 확인합니다."
  },
  programs: {
    label: "수업·활동 운영",
    description: "진로수업·체험·프로그램 참여 관리",
    help: "진로수업, 프로그램, 체험활동을 계획하고 학생 참여를 운영합니다."
  },
  assurance: {
    label: "업무 추천받기",
    description: "지금 확인할 정책·학교 업무를 정리",
    help: "정책과 학교 상황을 바탕으로 지금 확인이 필요한 업무를 정리해 보여줍니다."
  },
  caseload: {
    label: "학생 지원 Case",
    description: "장기 지원 학생의 계획과 진행상황 관리",
    help: "지속적인 지원이 필요한 학생의 목표, 지원계획, 실행과 결과를 관리합니다."
  },
  privacy: {
    label: "보호자 동의·공유",
    description: "동의 상태와 최소정보 공유를 관리",
    help: "보호자 동의와 개인정보 공유 목적·범위·유효기간을 확인하고 관리합니다."
  },
  partners: {
    label: "체험처·파트너",
    description: "외부기관 협력·일정·학생배치 관리",
    help: "외부기관과의 협력, 체험 일정 확약, 학생 배치와 후속업무를 관리합니다."
  },
  evidence: {
    label: "활동 검토하기",
    description: "학생 활동 근거를 확인하고 다음 단계로",
    help: "학생 활동의 근거를 검토하고 평가·성취·디지털배지 단계로 연결합니다."
  },
  badges: {
    label: "디지털배지 관리",
    description: "배지 부여·발급·수령 상태 확인",
    help: "디지털배지 부여, 발급상태, 학생 수령 여부와 회수·재발급을 관리합니다."
  },
  evaluation: {
    label: "평가·성취",
    description: "검증된 근거를 평가와 성취로 연결",
    help: "검증된 Evidence를 바탕으로 평가 결과와 성취를 확인합니다."
  },
  journeys: {
    label: "진로성장 여정",
    description: "학생 목표와 성장 흐름을 시간순으로 확인",
    help: "학생의 진로목표, 전환점, 다음 행동을 성장 여정으로 확인합니다."
  }
};

export function centerFriendlyNavCopy(
  routeKey: string,
  fallbackLabel: string,
  fallbackDescription?: string | null
): CenterFriendlyNavCopy {
  return CENTER_NAV_COPY[routeKey] || {
    label: fallbackLabel,
    description: fallbackDescription || "이 업무의 상세 내용을 확인합니다.",
    help: fallbackDescription || `${fallbackLabel} 업무를 확인하고 처리합니다.`
  };
}

const ACTION_HELP_RULES: Array<[RegExp, string]> = [
  [/로그아웃/, "현재 계정에서 로그아웃합니다."],
  [/새로고침|다시 불러오기/, "최신 상태로 다시 불러옵니다."],
  [/검색|찾기/, "입력한 조건에 맞는 정보를 찾습니다."],
  [/열기|보기|상세/, "관련 화면을 열어 상세 내용을 확인합니다."],
  [/저장|적용/, "입력하거나 선택한 내용을 저장해 반영합니다."],
  [/등록|추가/, "새 항목을 등록해 현재 업무에 추가합니다."],
  [/만들기|생성/, "새 항목을 만들어 다음 업무를 시작합니다."],
  [/보내기|배정|할당/, "선택한 대상에게 이 업무를 배정하거나 전달합니다."],
  [/승인|확정/, "현재 내용을 확인한 뒤 공식 상태로 확정합니다."],
  [/검토/, "현재 내용과 근거를 확인한 뒤 다음 단계를 결정합니다."],
  [/제출|요청/, "현재 내용을 담당자에게 제출하거나 처리를 요청합니다."],
  [/진행 시작|시작/, "이 업무를 진행 상태로 변경합니다."],
  [/완료|종료/, "현재 업무를 완료 상태로 변경합니다."],
  [/취소/, "현재 요청이나 작업을 취소합니다."],
  [/삭제|종료 처리/, "선택한 항목의 사용을 종료하거나 제거합니다."],
  [/회수/, "발급된 항목의 효력을 회수합니다."],
  [/재발급/, "기존 발급 이력을 기준으로 다시 발급을 요청합니다."],
  [/복사/, "표시된 정보를 클립보드에 복사합니다."],
  [/CSV|내보내기|다운로드/, "현재 데이터를 파일로 내보냅니다."],
  [/숨김/, "이 알림을 일정 기간 화면에서 숨깁니다."],
  [/다시 열기|재개/, "종료하거나 보류한 업무를 다시 진행합니다."],
  [/이동|전환/, "선택한 항목을 다음 상태 또는 화면으로 이동합니다."],
  [/동의/, "동의 상태와 공유 범위를 확인하거나 요청합니다."],
  [/배지/, "학생의 디지털배지 상태와 관련 업무를 처리합니다."],
  [/상담/, "학생 상담 기록과 후속일정을 확인하거나 관리합니다."],
  [/학생/, "선택한 학생의 기록과 관련 업무를 확인합니다."]
];

function compactLabel(element: HTMLElement) {
  return (
    element.getAttribute("aria-label")
    || element.getAttribute("title")
    || element.textContent
    || ""
  ).replace(/\s+/g, " ").trim().slice(0, 80);
}

function inferredHelp(element: HTMLElement) {
  const explicit = element.getAttribute("data-help");
  if (explicit) return explicit;

  const label = compactLabel(element);
  if (!label) return "이 기능을 실행합니다.";

  for (const [pattern, help] of ACTION_HELP_RULES) {
    if (pattern.test(label)) return help;
  }

  return `“${label}” 작업을 실행합니다.`;
}

type TooltipState = {
  text: string;
  left: number;
  top: number;
  placement: "right" | "top";
};

export function CenterTooltipLayer() {
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);

  useEffect(() => {
    function interactiveTarget(target: EventTarget | null) {
      if (!(target instanceof HTMLElement)) return null;
      return target.closest<HTMLElement>("button, a, [role='button'], [data-help]");
    }

    function show(target: EventTarget | null) {
      const element = interactiveTarget(target);
      if (!element || !element.closest("[data-center-ux-root='true']")) return;
      if (element.getAttribute("data-tooltip-disabled") === "true") return;

      const text = inferredHelp(element);
      if (!text) return;

      const rect = element.getBoundingClientRect();
      const sidebar = Boolean(element.closest(".sidebar"));
      const desktop = window.innerWidth > 760;

      if (sidebar && desktop) {
        setTooltip({
          text,
          left: Math.min(window.innerWidth - 330, rect.right + 12),
          top: Math.max(12, Math.min(window.innerHeight - 90, rect.top + rect.height / 2)),
          placement: "right"
        });
        return;
      }

      setTooltip({
        text,
        left: Math.max(150, Math.min(window.innerWidth - 150, rect.left + rect.width / 2)),
        top: Math.max(12, rect.top - 10),
        placement: "top"
      });
    }

    function hide() {
      setTooltip(null);
    }

    const handlePointerOver = (event: PointerEvent) => show(event.target);
    const handleFocusIn = (event: FocusEvent) => show(event.target);
    const handlePointerOut = (event: PointerEvent) => {
      const from = interactiveTarget(event.target);
      const to = interactiveTarget(event.relatedTarget);
      if (from && to === from) return;
      hide();
    };
    const handleFocusOut = (event: FocusEvent) => {
      const from = interactiveTarget(event.target);
      const to = interactiveTarget(event.relatedTarget);
      if (from && to === from) return;
      hide();
    };

    document.addEventListener("pointerover", handlePointerOver);
    document.addEventListener("focusin", handleFocusIn);
    document.addEventListener("pointerout", handlePointerOut);
    document.addEventListener("focusout", handleFocusOut);
    window.addEventListener("scroll", hide, true);
    window.addEventListener("resize", hide);

    return () => {
      document.removeEventListener("pointerover", handlePointerOver);
      document.removeEventListener("focusin", handleFocusIn);
      document.removeEventListener("pointerout", handlePointerOut);
      document.removeEventListener("focusout", handleFocusOut);
      window.removeEventListener("scroll", hide, true);
      window.removeEventListener("resize", hide);
    };
  }, []);

  if (!tooltip) return null;

  return (
    <div
      className={`center-global-tooltip ${tooltip.placement}`}
      role="tooltip"
      style={{ left: tooltip.left, top: tooltip.top }}
    >
      {tooltip.text}
    </div>
  );
}
